import type { ListerProposal } from "../../../lib/api";
import { fmtCents } from "../../../lib/format";
import { BUY_TRADE_FIXED_NOTE, priceFloorCents } from "../../../lib/lister-state";
import NumberField from "./NumberField";

export default function PriceCard({ price, priceCents, costCents, buyTradePriceCents = null, costLocked = false, researched = true, onPrice, onCost }: {
  price: ListerProposal["price"]; priceCents: number; costCents: number | null;
  /** Buy / Trade's own price for this item — null for every non-Buy/Trade session. Shown as a
   *  second choice beside the Lister's own research price (`price.suggested_cents`); either
   *  button just dispatches the same price_cents change the manual field already does. */
  buyTradePriceCents?: number | null;
  /** True for a Buy / Trade session — cost is Buy / Trade's own and is not editable here. */
  costLocked?: boolean;
  /** False for an item session (send-to-lister task L6): a lot or other item is never
   *  researched, so there is no research price to offer as a choice and no MSRP, sold or active
   *  figures to show — only Buy / Trade's price, the price field and the cost floor. */
  researched?: boolean;
  onPrice: (c: number | null) => void; onCost: (c: number | null) => void;
}) {
  // X2 (send-to-lister cross-app review): the SAME function validateMerged enforces the floor
  // with, rounded up to the whole dollar — not cost × 1.25 to the cent — so this line and the
  // raise button below always match the error staff would actually get.
  const floor = costCents !== null ? priceFloorCents(costCents) : null;
  // Pricing found nothing trustworthy: the proposal carries a sentinel of 0, and the server
  // blocks posting until the operator sets a real price. A real price is always > 0 (client
  // and server validation both enforce this), so an untouched 0 here is never a genuine
  // proposal — showing it as "$0.00" would read as one. Say so instead of formatting a zero.
  const noPrice = priceCents === 0;
  return (
    <div className="space-y-2 font-mono text-sm">
      {buyTradePriceCents !== null && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Price choice">
          <button type="button" aria-pressed={priceCents === buyTradePriceCents} onClick={() => onPrice(buyTradePriceCents)} className="text-xs bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded">Buy / Trade: {fmtCents(buyTradePriceCents)}</button>
          {/* M12 (send-to-lister review): price.suggested_cents is the proposal's own 0
              sentinel for "nothing found" (never a real price) when research came up empty —
              most often an item session, which isn't researched at all (researched=false,
              already excluded below). Offering it as a choice reads as "research says $0.00". */}
          {researched && price.suggested_cents > 0 && <button type="button" aria-pressed={priceCents === price.suggested_cents} onClick={() => onPrice(price.suggested_cents)} className="text-xs bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded">Research: {fmtCents(price.suggested_cents)}</button>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="block text-xs text-zinc-400 mb-1">Price</span>
          <NumberField kind="money" value={noPrice ? null : priceCents} onCommit={onPrice} ariaLabel="Price" className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2" />
          {noPrice && <p className="text-amber-300 text-xs mt-1">No price proposed — set one.</p>}
        </label>
        <label className="block"><span className="block text-xs text-zinc-400 mb-1">Cost (optional)</span>
          {costLocked
            ? <><input aria-label="Cost" value={fmtCents(costCents)} readOnly className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 read-only:opacity-60 read-only:cursor-default" /><p className="text-zinc-500 text-xs mt-1">{BUY_TRADE_FIXED_NOTE}</p></>
            : <NumberField kind="money" allowNull value={costCents} onCommit={onCost} ariaLabel="Cost" className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2" />}
        </label>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
        {researched && <><span>MSRP {fmtCents(price.msrp_cents)}</span><span>sold median {fmtCents(price.sold_median_cents)} × {price.sold_n}</span><span>active low {fmtCents(price.active_low_cents)}</span></>}<span>floor {fmtCents(floor)}</span>
      </div>
      <div className="text-xs text-zinc-500">{price.basis}</div>
      {floor !== null && floor > priceCents && <button onClick={() => onPrice(floor)} className="text-xs bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded">raise to cost × 1.25 ({fmtCents(floor)})</button>}
    </div>
  );
}
